import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import {
  HiOutlineArrowTopRightOnSquare,
  HiOutlineArrowUturnLeft,
  HiOutlineCheckCircle,
  HiOutlineExclamationTriangle,
  HiOutlineNoSymbol,
} from "react-icons/hi2";
import CustomButton from "components/common/Button/CustomButton";
import AvatarGroup from "components/common/Avatar/AvatarGroup";
import {
  applyAvatarImageFallback,
  baseURLImage,
  capitalizeAllLetters,
  getNameProfile,
} from "Utils/utils";
import {
  buildTimesheetConfirmDetails,
  getTimesheetConfirmSummaryRows,
} from "../../utils/timesheetConfirmDetails";
import {
  getBulkUnapproveBlockMessage,
  getBulkUnapproveBlockTitle,
  getBulkUnapproveRowOutcome,
  summarizeBulkUnapproveRows,
} from "../../utils/timesheetBulkUnapprove";
import TimesheetStatusPill from "./TimesheetStatusPill";
import useCareLabels from "hooks/useCareLabels";

export const buildUnapproveTimesheetDetails = buildTimesheetConfirmDetails;

const UNAPPROVE_CONFIRM_BTN_CLASS =
  "!min-w-[7.5rem] justify-center !bg-red-200 !border-red-200 !text-red-800 hover:!bg-red-100 hover:!border-red-100 disabled:hover:!bg-red-200 disabled:hover:!border-red-200 transition-colors duration-150";

const UNAPPROVE_FUNDING_CONFIRM_BTN_CLASS =
  "!min-w-[7.5rem] justify-center !bg-[#D97706] !border-[#D97706] !text-white hover:!bg-[#F59E0B] hover:!border-[#F59E0B] disabled:hover:!bg-[#D97706] disabled:hover:!border-[#D97706] !shadow-sm transition-colors duration-150";

const BADGE_FLEX_BASE =
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap leading-none";

const SUMMARY_CHIP_CLASS = `${BADGE_FLEX_BASE} h-7 rounded-full border px-3 text-xs font-semibold normal-case`;

const OUTCOME_BADGE_CLASS = `${BADGE_FLEX_BASE} h-6 rounded-md border px-2 text-[11px] font-bold uppercase tracking-wide`;

const BULK_TABLE_SCROLL_ROW_THRESHOLD = 6;

const BadgeIcon = ({ icon: Icon }) => (
  <Icon className="block h-3.5 w-3.5 shrink-0" aria-hidden />
);

const BadgeLabel = ({ children, className = "" }) => (
  <span className={`block leading-none ${className}`.trim()}>{children}</span>
);

const handleParticipantImageError = (event, firstName, lastName) => {
  applyAvatarImageFallback(event, firstName, lastName);
};

const BulkRowParticipants = ({ participants = [] }) => {
  if (!participants.length) {
    return <span className="text-[#94A3B8]">—</span>;
  }

  if (participants.length === 1) {
    const participant = participants[0];
    const inactive = participant?.status !== "active";

    return (
      <div
        className={`flex min-w-0 items-center gap-1 ${
          inactive ? "opacity-50 text-red-500" : ""
        }`}
        title={
          inactive
            ? capitalizeAllLetters(
                `${participant?.first_name} ${participant?.last_name} (Inactive)`
              )
            : capitalizeAllLetters(
                `${participant?.first_name} ${participant?.last_name}`
              )
        }
      >
        <div className="h-9 w-9 shrink-0">
          <img
            onError={(event) =>
              handleParticipantImageError(
                event,
                participant?.first_name,
                participant?.last_name
              )
            }
            className="h-9 w-9 rounded-full object-cover"
            src={
              participant?.profile_picture
                ? baseURLImage(participant.profile_picture)
                : getNameProfile(
                    participant?.first_name,
                    participant?.last_name
                  )
            }
            alt=""
          />
        </div>
        <span className="min-w-0 flex-1 break-words">
          {capitalizeAllLetters(
            `${participant?.first_name} ${participant?.last_name}`
          )}
        </span>
      </div>
    );
  }

  return (
    <AvatarGroup
      type="participant"
      arrayData={participants}
      cssClass="mt-0"
      maxLength={3}
      isNoRing
    />
  );
};

const BulkSummaryChips = ({ summary }) => {
  if (!summary?.total) return null;

  const chips = [];
  if (summary.ready > 0) {
    chips.push({
      key: "ready",
      label: `${summary.ready} ready to unapprove`,
      className: "border-[#86EFAC] bg-[#F0FDF4] text-[#166534]",
      icon: HiOutlineCheckCircle,
    });
  }
  if (summary.blocked > 0) {
    chips.push({
      key: "blocked",
      label: `${summary.blocked} blocked`,
      className: "border-[#FCA5A5] bg-[#FEF2F2] text-[#B91C1C]",
      icon: HiOutlineNoSymbol,
    });
  }
  if (summary.fundingWarning > 0) {
    chips.push({
      key: "funding",
      label: `${summary.fundingWarning} need funding review`,
      className: "border-[#FCD34D] bg-[#FFFBEB] text-[#B45309]",
      icon: HiOutlineExclamationTriangle,
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-[#E5E7EB] bg-[#F8FAFC] px-5 py-3 sm:px-6">
      <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
        Summary
      </span>
      {chips.map((chip) => {
        const Icon = chip.icon;
        return (
          <span
            key={chip.key}
            className={`${SUMMARY_CHIP_CLASS} ${chip.className}`}
          >
            <BadgeIcon icon={Icon} />
            <BadgeLabel>{chip.label}</BadgeLabel>
          </span>
        );
      })}
    </div>
  );
};

const BulkRowOutcome = ({ row }) => {
  const navigate = useNavigate();
  const outcome = getBulkUnapproveRowOutcome(row);

  if (outcome === "blocked") {
    const isInvoiceBlock = row.blockReason === "invoice_generated";

    return (
      <div className="space-y-2">
        <span
          className={`${OUTCOME_BADGE_CLASS} border-[#FECACA] bg-[#FEF2F2] text-[#DC2626]`}
        >
          <BadgeIcon icon={HiOutlineNoSymbol} />
          <BadgeLabel>{getBulkUnapproveBlockTitle(row.blockReason)}</BadgeLabel>
        </span>
        <p className="text-[11px] leading-snug text-[#B91C1C]">
          {getBulkUnapproveBlockMessage(row.blockReason, row.invoiceNumber)}
        </p>
        {isInvoiceBlock ? (
          <button
            type="button"
            onClick={() => navigate("/rostering/invoicing")}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#2563EB] underline-offset-2 hover:text-[#1D4ED8] hover:underline"
          >
            Go to invoices
            <HiOutlineArrowTopRightOnSquare className="h-3.5 w-3.5 shrink-0" />
          </button>
        ) : null}
      </div>
    );
  }

  if (outcome === "funding_warning") {
    return (
      <div className="space-y-1">
        <span
          className={`${OUTCOME_BADGE_CLASS} border-[#FDE68A] bg-[#FFFBEB] text-[#B45309]`}
        >
          <BadgeIcon icon={HiOutlineExclamationTriangle} />
          <BadgeLabel>Check funding</BadgeLabel>
        </span>
        <p className="text-[11px] leading-snug text-[#92400E]">
          {row.ambiguousCount} ambiguous line
          {row.ambiguousCount === 1 ? "" : "s"}
        </p>
      </div>
    );
  }

  return (
    <span
      className={`${OUTCOME_BADGE_CLASS} border-[#FECACA] bg-[#FEF2F2] text-[#DC2626]`}
    >
      <BadgeIcon icon={HiOutlineArrowUturnLeft} />
      <BadgeLabel>Will unapprove</BadgeLabel>
    </span>
  );
};

const BulkUnapproveTable = ({ rows = [], participantColumnLabel = "Participants" }) => {
  const enableScroll = rows.length >= BULK_TABLE_SCROLL_ROW_THRESHOLD;

  return (
    <div className="border-t border-[#E5E7EB]">
      <div
        className={
          enableScroll
            ? "custom-scroll max-h-[min(23rem,52vh)] overflow-y-auto overflow-x-auto"
            : "overflow-x-auto"
        }
      >
      <table className="min-w-full text-left text-xs">
        <thead className="sticky top-0 z-[1] bg-white">
          <tr className="border-b border-[#E5E7EB]">
            {[
              "Worker",
              participantColumnLabel,
              "Shift date",
              "Schedule",
              "Actual",
              "Status",
              "Outcome",
            ].map((label) => (
              <th
                key={label}
                className="whitespace-nowrap px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wide text-[#94A3B8]"
              >
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[#E5E7EB] bg-white">
          {rows.map((row) => {
            const outcome = getBulkUnapproveRowOutcome(row);
            const rowClass =
              outcome === "blocked"
                ? "bg-[#FEF2F2]/50"
                : outcome === "funding_warning"
                ? "bg-[#FFFBEB]/40"
                : "hover:bg-[#F8FAFC]/80";

            return (
              <tr key={row.uuid} className={rowClass}>
                <td className="px-5 py-3.5 align-top">
                  <p className="font-semibold text-[#0F172A]">{row.workerName}</p>
                </td>
                <td className="min-w-[9rem] px-5 py-3.5 align-top">
                  <BulkRowParticipants participants={row.participants} />
                </td>
                <td className="whitespace-nowrap px-5 py-3.5 align-top font-medium text-[#0F172A]">
                  {row.details?.shiftDate ?? "—"}
                </td>
                <td className="whitespace-nowrap px-5 py-3.5 align-top font-medium text-[#0F172A]">
                  {row.details?.scheduled ?? "—"}
                </td>
                <td className="whitespace-nowrap px-5 py-3.5 align-top font-medium text-[#0F172A]">
                  {row.details?.actual ?? "—"}
                </td>
                <td className="px-5 py-3.5 align-top">
                  <TimesheetStatusPill status={row.status} />
                </td>
                <td className="min-w-[9.5rem] max-w-[14rem] px-5 py-3.5 align-top">
                  <BulkRowOutcome row={row} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
  );
};

const TimesheetUnapproveConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  confirming = false,
  requiresFundingWarning = false,
  ambiguousCount = 0,
  details,
  variant = "single",
  bulkRows = [],
  eligibleCount = 0,
  blockedCount = 0,
}) => {
  const isBulk = variant === "bulk";
  const { participantLabelPlural, careSector } = useCareLabels();
  const bulkSummary = useMemo(
    () => summarizeBulkUnapproveRows(bulkRows),
    [bulkRows]
  );
  const summaryRows = useMemo(
    () => getTimesheetConfirmSummaryRows(careSector),
    [careSector]
  );

  const handleClose = () => {
    if (!confirming) onClose?.();
  };

  const title = requiresFundingWarning
    ? isBulk
      ? "Bulk unapprove — check funding"
      : "Unapprove — check funding"
    : isBulk
    ? "Unapprove selected timesheets?"
    : "Unapprove timesheet?";

  const introText = useMemo(() => {
    if (!isBulk) {
      return details
        ? "You are about to unapprove this timesheet:"
        : "You are about to move this timesheet back to pending.";
    }

    if (eligibleCount === 0) {
      return "None of the selected timesheets can be unapproved.";
    }

    if (blockedCount > 0) {
      return `${eligibleCount} of ${bulkRows.length} selected timesheet${
        bulkRows.length === 1 ? "" : "s"
      } will move back to pending. Blocked rows are listed below and will be skipped.`;
    }

    return `All ${eligibleCount} selected timesheet${
      eligibleCount === 1 ? "" : "s"
    } will move back to pending.`;
  }, [isBulk, details, eligibleCount, blockedCount, bulkRows.length]);

  const confirmDisabled =
    confirming || (isBulk && (eligibleCount === 0 || bulkRows.length === 0));

  const confirmButtonClass = requiresFundingWarning
    ? UNAPPROVE_FUNDING_CONFIRM_BTN_CLASS
    : UNAPPROVE_CONFIRM_BTN_CLASS;

  const confirmLabel = useMemo(() => {
    if (confirming) {
      if (isBulk) {
        return `Unapproving ${eligibleCount} timesheet${
          eligibleCount === 1 ? "" : "s"
        }…`;
      }
      return "Unapproving...";
    }

    if (isBulk) {
      return `Unapprove ${eligibleCount} timesheet${
        eligibleCount === 1 ? "" : "s"
      }`;
    }
    return "Unapprove";
  }, [confirming, isBulk, eligibleCount]);

  const modalFooter = (
    <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm font-medium text-[#0F172A]">
        {isBulk && eligibleCount === 0
          ? "Close and fix blocked timesheets first."
          : "Do you want to continue?"}
      </p>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <CustomButton
          type="button"
          variant="secondary"
          label="No, go back"
          clickHandler={handleClose}
          isDisabled={confirming}
          cssClass="!min-w-[7.5rem] justify-center"
        />
        {!(isBulk && eligibleCount === 0) ? (
          <CustomButton
            type="button"
            variant={requiresFundingWarning ? "tertiary" : "danger"}
            label={confirmLabel}
            Icon={
              !confirming ? (
                <HiOutlineArrowUturnLeft className="h-4 w-4" />
              ) : null
            }
            clickHandler={onConfirm}
            isDisabled={confirmDisabled}
            showLoading={confirming}
            loaderCssClass={
              requiresFundingWarning ? "!text-white !mr-0" : "!text-red-800 !mr-0"
            }
            cssClass={`${confirmButtonClass}${
              confirming ? " !opacity-100" : ""
            }`}
          />
        ) : null}
      </div>
    </div>
  );

  return (
    <ModalSkeleton
      isOpen={isOpen}
      title={title}
      closeModal={confirming ? null : handleClose}
      cssClass={
        isBulk
          ? "w-full max-w-6xl sm:my-8"
          : "w-full max-w-lg sm:my-8"
      }
      bodyPadding={isBulk ? "!p-0" : "!p-4 sm:!p-5"}
      modalFooter={modalFooter}
    >
      {isBulk ? (
        <div>
          <div className="px-5 py-4 sm:px-6">
            <p className="text-sm leading-relaxed text-[#64748B]">{introText}</p>
          </div>

          <BulkSummaryChips summary={bulkSummary} />

          <BulkUnapproveTable
            rows={bulkRows}
            participantColumnLabel={participantLabelPlural}
          />

          {blockedCount > 0 ? (
            <p className="border-t border-[#E5E7EB] px-5 py-3 text-xs leading-relaxed text-[#64748B] sm:px-6">
              <span className="font-semibold text-[#DC2626]">Blocked</span> rows
              cannot be unapproved until the issue is resolved (for example,
              they are not in an unapprovable status).
            </p>
          ) : null}

          {requiresFundingWarning ? (
            <div className="mx-5 mb-4 mt-4 flex gap-3 rounded-xl border border-[#F5D78E] bg-[#FEF3C7] px-4 py-3 sm:mx-6">
              <HiOutlineExclamationTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[#D97706]" />
              <div className="min-w-0 space-y-1.5 text-sm text-[#92400E]">
                <p className="font-bold text-[#0F172A]">
                  Funding (core roster link is on)
                </p>
                <ul className="list-disc space-y-1 pl-4 leading-relaxed">
                  <li>
                    {ambiguousCount} invoice line
                    {ambiguousCount === 1 ? "" : "s"} across the selected timesheet
                    {eligibleCount !== 1 ? "s" : ""} can match more than one active
                    funding.
                  </li>
                  <li>
                    Unapprove still reverses budgets using the snapshot saved at
                    approval, or legacy latest-funding rules if there is no snapshot.
                  </li>
                </ul>
                <p className="leading-relaxed">
                  Review the list above, then confirm only if you accept that risk.
                </p>
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm leading-relaxed text-[#64748B]">{introText}</p>

          {details ? (
            <div className="overflow-hidden rounded-xl border border-[#E5E7EB] bg-[#F8FAFC]">
              <div className="border-b border-[#E5E7EB] bg-[#EEF2FB]/70 px-4 py-2.5">
                <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#4A6BB6]">
                  Timesheet summary
                </p>
              </div>
              <dl className="divide-y divide-[#E5E7EB]">
                {summaryRows.map(({ key, label }) => (
                  <div
                    key={key}
                    className="flex flex-col gap-0.5 px-4 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
                  >
                    <dt className="text-xs font-semibold text-[#64748B]">{label}</dt>
                    <dd className="text-sm font-semibold text-[#0F172A] sm:max-w-[62%] sm:text-right">
                      {details?.[key] ?? "—"}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : null}

          {requiresFundingWarning ? (
            <div className="flex gap-3 rounded-xl border border-[#F5D78E] bg-[#FEF3C7] px-4 py-3">
              <HiOutlineExclamationTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[#D97706]" />
              <div className="min-w-0 space-y-1.5 text-sm text-[#92400E]">
                <p className="font-bold text-[#0F172A]">
                  Funding (core roster link is on)
                </p>
                <ul className="list-disc space-y-1 pl-4 leading-relaxed">
                  <li>
                    {ambiguousCount} invoice line
                    {ambiguousCount === 1 ? "" : "s"} across the selected timesheet
                    can match more than one active funding.
                  </li>
                  <li>
                    Unapprove still reverses budgets using the snapshot saved at
                    approval, or legacy latest-funding rules if there is no snapshot.
                  </li>
                </ul>
                <p className="leading-relaxed">
                  Review the list above, then confirm only if you accept that risk.
                </p>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </ModalSkeleton>
  );
};

export default TimesheetUnapproveConfirmModal;
